/* eslint-disable no-param-reassign */

import _ from 'underscore';

import { createViewClass, createUpdateEntityView, getTemplateWithUrlFromModel, createOptionsViewClass, createUETSelectorClass } from '../options-section/index';
import { NewFields } from './advanced/new-fields';
import { ExistingFields } from './advanced/existing-fields';

export class EntityFields {
  constructor({
    i18n,
    model,
    flattenedSchemaGenerator,
    subFieldsBlacklist,
    isPageFeedEnabled,
    isMultiAccountImport,
    UetTagSelectorView,
    hideAdvancedOptions,
    isGoogleImageExtensionImportOptionEnabled,
    isGoogleLogoExtensionImportOptionEnabled,
    isGoogleUpdateAdUrlsImportOptionEnabled,
    isGoogleUpdateSitelinkUrlsImportOptionEnabled,
    isGoogleAccountNegativeKeywordsImportOptionEnabled,
    isGoogleAdCustomizerAttributesImportOptionEnabled,
    isGoogleConversionGoalsImportOptionEnabled,
    isBrandSuitabilityImportOptionEnabled,
  }) {
    this.i18n = i18n;
    this.model = model;
    this.subFieldsBlacklist = subFieldsBlacklist;
    this.UetTagSelectorView = UetTagSelectorView;
    this.hideAdvancedOptions = hideAdvancedOptions;

    this.isMultiAccountImport = isMultiAccountImport;

    const newItemsSchema = flattenedSchemaGenerator.getFlattenedSchemaWithProperties('ImportNewOption');
    const existingItemsSchema = flattenedSchemaGenerator.getFlattenedSchemaWithProperties('ImportUpdateEntityPreference');
    _.extend(newItemsSchema.properties, flattenedSchemaGenerator.getFlattenedSchemaWithProperties('ImportNewLabelsAndAssociations').properties);
    _.extend(existingItemsSchema.properties, flattenedSchemaGenerator.getFlattenedSchemaWithProperties('UpdateLabelsAndAssociations').properties);
    _.extend(newItemsSchema.properties, flattenedSchemaGenerator.getFlattenedSchemaWithProperties('ImportNewCustomFiltersForShoppingCampaigns').properties);
    _.extend(existingItemsSchema.properties, flattenedSchemaGenerator.getFlattenedSchemaWithProperties('UpdateCustomFiltersForShoppingCampaigns').properties);

    const newFieldsGenerator = new NewFields({
      i18n,
      isPageFeedEnabled,
      isGoogleImageExtensionImportOptionEnabled,
      isGoogleLogoExtensionImportOptionEnabled,
      isGoogleAccountNegativeKeywordsImportOptionEnabled,
      isGoogleConversionGoalsImportOptionEnabled,
      isBrandSuitabilityImportOptionEnabled,
    });
    const existingFieldsGenerator = new ExistingFields({
      i18n,
      isPageFeedEnabled,
      isGoogleImageExtensionImportOptionEnabled,
      isGoogleLogoExtensionImportOptionEnabled,
      isGoogleUpdateAdUrlsImportOptionEnabled,
      isGoogleUpdateSitelinkUrlsImportOptionEnabled,
      isGoogleAdCustomizerAttributesImportOptionEnabled,
      isGoogleConversionGoalsImportOptionEnabled,
      isBrandSuitabilityImportOptionEnabled,
    });

    const newFeedFields = newFieldsGenerator.getNewFeedFields();
    const existingFeedFields = existingFieldsGenerator.getExistingFeedFields();
    const newConversionFields = newFieldsGenerator.getNewConversionFields();
    const existingConversionFields = existingFieldsGenerator.getExistingConversionFields();

    this.newSubSectionsConfig = _.compact([
      {
        advancedOptionsFields: newFieldsGenerator.getImportItemsFields(),
        advancedOptionsSchema: newItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_ItemsSectionHeader'),
      },
      {
        advancedOptionsFields: newFieldsGenerator.getNewTargetingFields(),
        advancedOptionsSchema: newItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_LocationTarget'),
      },
      {
        advancedOptionsFields: newFieldsGenerator.getNewAdExtensionFields(),
        advancedOptionsSchema: newItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_AdExtensions'),
      },
      !_.isEmpty(newFeedFields) && {
        advancedOptionsFields: newFeedFields,
        advancedOptionsSchema: newItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_Feeds'),
      },
      !_.isEmpty(newConversionFields) && {
        advancedOptionsFields: newConversionFields,
        advancedOptionsSchema: newItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_ConversionGoals'),
      },
    ]);

    this.updateSubSectionsConfig = _.compact([
      {
        advancedOptionsFields: existingFieldsGenerator.getOptionalItemFields(),
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('BulkEdit_Campaigns_BulkEditTitle'),
      },
      {
        advancedOptionsFields: existingFieldsGenerator.getExistingTargetingFields(),
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_LocationTarget'),
      },
      {
        advancedOptionsFields: existingFieldsGenerator.getExistingAdExtensionFields(),
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_AdExtensions'),
      },
      !_.isEmpty(existingFeedFields) && {
        advancedOptionsFields: existingFeedFields,
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_Feeds'),
      },
      {
        advancedOptionsFields: existingFieldsGenerator.getStatusFields(),
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('GoogleImport_SelectCampaign_Status'),
      },
      !_.isEmpty(existingConversionFields) && {
        advancedOptionsFields: existingConversionFields,
        advancedOptionsSchema: existingItemsSchema,
        includeAllOption: true,
        label: this.i18n.getString('Import_ImportOptions_ConversionGoals'),
      },
    ]);
  }

  removeFieldsOnBlacklist(subSectionsConfig) {
    _.each(subSectionsConfig, (config) => {
      config.advancedOptionsFields = _.reject(config.advancedOptionsFields, subField => (
        _.contains(this.subFieldsBlacklist, subField.property)
      ));
    });

    return _.reject(subSectionsConfig, config => _.isEmpty(config.advancedOptionsFields));
  }

  getEntitySubFields(helpIds = {}) {
    this.newSubSectionsConfig = this.removeFieldsOnBlacklist(this.newSubSectionsConfig);
    this.updateSubSectionsConfig = this.removeFieldsOnBlacklist(this.updateSubSectionsConfig);

    return _.compact([{
      property: 'ImportAccountUrlOptions',
      control: this.model.isCustomerLevel ? createOptionsViewClass({
        i18n: this.i18n,
        model: this.model,
      }) : getTemplateWithUrlFromModel(this.i18n, this.model),
      showLabel: false,
    },
    {
      property: 'ImportNewEntities',
      control: createViewClass({
        subSectionsConfig: this.newSubSectionsConfig,
        sectionLabel: this.i18n.getString('Import_ImportOptions_ImportNew'),
        model: this.model,
        i18n: this.i18n,
        helpId: helpIds.ImportNewEntities || 'Pop_BA_Import_ItemsNotPreviouslyImported',
        hideAdvancedOptions: this.hideAdvancedOptions,
      }),
      showLabel: false,
    },
    {
      property: 'UpdateEntityPreferences',
      control: createUpdateEntityView({
        subSectionsConfig: this.updateSubSectionsConfig,
        sectionLabel: this.i18n.get('Import_ImportOptions_ExistingItems'),
        model: this.model,
        i18n: this.i18n,
        hideAdvancedOptions: this.hideAdvancedOptions,
        helpId: helpIds.UpdateEntityPreferences,
      }),
      showLabel: false,
    },
    {
      property: 'ImportDeletedEntities',
      message: this.i18n.get('ImportDeletedEntities'),
      showLabel: false,
    },
    {
      property: 'ImportUET',
      message: this.i18n.get('Import_ImportOptions_UETTag'),
      showLabel: true,
      label: this.i18n.get('Import_ImportOptions_UETTag_Label'),
    },
    {
      property: 'AssociatedUETTagId',
      showLabel: false,
      control: this.UetTagSelectorView ?
        createUETSelectorClass(this.UetTagSelectorView, this.isMultiAccountImport ? 'Customer' : 'Account') : undefined,
    }]);
  }

  getEntityFields(helpIds) {
    return [
      {
        type: 'collapse',
        text: this.i18n.getString('Import_ImportOptions_WhatToImport'),
        isExpanded: true,
        fields: this.getEntitySubFields(helpIds),
      },
    ];
  }
}
